/*
 * Firebase glue: app init from /firebase-config.json, auth state, and the
 * Firestore layout backend (users/{uid}/layouts/{name} → {data, mtime}).
 *
 * The config file is fetched at runtime so switching Firebase projects never
 * needs a rebuild. If the file is missing or still holds placeholder values,
 * cloud mode is simply unavailable and the app runs device-only.
 *
 * The sign-in UI (FirebaseUI) lives in authui.ts so the VR bundle can use
 * this module without pulling that in.
 */
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import type { LayoutMeta, LoadResult, StoreBackend, VersionMeta } from './store.ts';
import { MAX_VERSIONS } from './versions.ts';

let app: firebase.app.App | null = null;

export async function initCloud(): Promise<boolean> {
  if (app) return true;
  try {
    const res = await fetch('/firebase-config.json', { cache: 'no-store' });
    if (!res.ok) return false;
    const cfg = await res.json();
    if (!cfg.apiKey || /paste|your-/i.test(String(cfg.apiKey))) return false;
    app = firebase.initializeApp(cfg);
    // tests: a config that names local emulators (the test server serves one)
    if (cfg.emulators) {
      const [fh, fp] = String(cfg.emulators.firestore).split(':');
      firebase.firestore().useEmulator(fh, Number(fp));
      firebase.auth().useEmulator(String(cfg.emulators.auth));
      (window as any).__testAuth = {
        signUp: (email: string, pw: string) => firebase.auth().createUserWithEmailAndPassword(email, pw),
        signIn: (email: string, pw: string) => firebase.auth().signInWithEmailAndPassword(email, pw),
        signOut: () => firebase.auth().signOut(),
        offline: (on: boolean) => on ? firebase.firestore().disableNetwork() : firebase.firestore().enableNetwork(),
      };
    }
    // offline persistence: edits made without a connection queue up and sync
    // later; a second tab of the same account shares the cache
    try { await firebase.firestore().enablePersistence({ synchronizeTabs: true }); } catch { /* unsupported or another tab owns it: fine */ }
    return true;
  } catch { return false; }
}

export const cloudReady = (): boolean => !!app;
export const auth = () => firebase.auth();
export const currentUser = () => (app ? firebase.auth().currentUser : null);

/** Subscribe to auth changes; fires immediately (with null when cloud is off). */
export function onAuth(cb: (user: firebase.User | null) => void): void {
  if (!app) { cb(null); return; }
  firebase.auth().onAuthStateChanged(u => cb(u));
}

export const signOut = (): Promise<void> => firebase.auth().signOut();

const byMtimeDesc = (a: LayoutMeta, b: LayoutMeta) => b.mtime - a.mtime || a.name.localeCompare(b.name);
/** A layout doc that actually holds a layout (not just share flags). */
const hasLayout = (d: firebase.firestore.QueryDocumentSnapshot) => typeof d.data().data === 'string';

export class CloudBackend implements StoreBackend {
  kind = 'cloud' as const;
  constructor(private uid: string) {}

  private col() {
    return firebase.firestore().collection('users').doc(this.uid).collection('layouts');
  }

  async list(): Promise<LayoutMeta[]> {
    const snap = await this.col().get();
    return snap.docs.filter(hasLayout).map(d => ({ name: d.id, mtime: (d.data().mtime as number) || 0 })).sort(byMtimeDesc);
  }

  async load(name: string): Promise<LoadResult | null> {
    const d = await this.col().doc(name).get();
    if (!d.exists) return null;
    const x = d.data()!;
    try { return { doc: JSON.parse(x.data), mtime: x.mtime || 0 }; } catch { return null; }
  }

  async save(name: string, json: string, mtime?: number): Promise<number> {
    const m = mtime ?? Date.now() / 1000;
    // merge: keep share flags (shared/shareId) intact across saves
    await this.col().doc(name).set({ data: json, mtime: m }, { merge: true });
    return m;
  }

  async remove(name: string): Promise<void> {
    // the share pointer and the version history go with it, so nothing dangles
    const ref = this.col().doc(name);
    const d = await ref.get();
    const shareId = d.exists ? (d.data()!.shareId as string | undefined) : undefined;
    const batch = firebase.firestore().batch();
    if (shareId) batch.delete(firebase.firestore().collection('shares').doc(shareId));
    (await this.vcol(name).get()).docs.forEach(v => batch.delete(v.ref));
    batch.delete(ref);
    await batch.commit();
  }

  private vcol(name: string) { return this.col().doc(name).collection('versions'); }

  async listVersions(name: string): Promise<VersionMeta[]> {
    const snap = await this.vcol(name).orderBy('mtime', 'desc').limit(MAX_VERSIONS).get();
    return snap.docs.map(d => ({ id: d.id, mtime: (d.data().mtime as number) || 0 }));
  }

  async loadVersion(name: string, id: string): Promise<LoadResult | null> {
    const d = await this.vcol(name).doc(id).get();
    if (!d.exists) return null;
    const x = d.data()!;
    try { return { doc: JSON.parse(x.data), mtime: x.mtime || 0 }; } catch { return null; }
  }

  async saveVersion(name: string, json: string, mtime: number): Promise<void> {
    await this.vcol(name).doc(String(Math.round(mtime * 1000))).set({ data: json, mtime });
    const all = await this.vcol(name).orderBy('mtime', 'desc').get();
    if (all.size > MAX_VERSIONS) {
      const batch = firebase.firestore().batch();
      all.docs.slice(MAX_VERSIONS).forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  }

  watchList(cb: (list: LayoutMeta[]) => void): () => void {
    return this.col().onSnapshot(snap => {
      cb(snap.docs.filter(hasLayout).map(d => ({ name: d.id, mtime: (d.data().mtime as number) || 0 })).sort(byMtimeDesc));
    }, () => { /* transient listen error; next snapshot recovers */ });
  }
}

/* ---------------- edit invites / collaborators ----------------
 * invites/{code} = {uid: owner, layouts, exp}: the owner mints one for a
 * layout; whoever opens /edit?invite=<code> while signed in writes their own
 * grants/{uid} = {uid: owner, mode: 'edit', invite, layouts, …} (the rules
 * check it against the invite) and can then edit exactly those layouts. */
const INVITE_TTL_MS = 7 * 24 * 3600 * 1000;
const EDIT_GRANT_TTL_MS = 90 * 24 * 3600 * 1000;
export interface Invite { uid: string; layouts: string[]; exp: number }

export async function createInvite(uid: string, layouts: string[]): Promise<string> {
  const code = newShareId();
  await firebase.firestore().collection('invites').doc(code)
    .set({ uid, layouts, exp: Date.now() + INVITE_TTL_MS, expires: ttlStamp(INVITE_TTL_MS), created: Date.now() });
  return code;
}
export async function resolveInvite(code: string): Promise<Invite | null> {
  try {
    const d = await firebase.firestore().collection('invites').doc(code).get();
    const x = d.exists ? d.data()! : null;
    return x && x.exp > Date.now() ? { uid: x.uid, layouts: x.layouts || [], exp: x.exp } : null;
  } catch { return null; }
}
/** Accept an invite as the signed-in user; returns it (or null if dead). */
export async function acceptInvite(code: string, me: { uid: string; editor: string }): Promise<Invite | null> {
  const inv = await resolveInvite(code);
  if (!inv) return null;
  await firebase.firestore().collection('grants').doc(me.uid).set({
    uid: inv.uid, mode: 'edit', invite: code, layouts: inv.layouts,
    exp: Date.now() + EDIT_GRANT_TTL_MS, expires: ttlStamp(EDIT_GRANT_TTL_MS), editor: me.editor.slice(0, 100), created: Date.now(),
  });
  return inv;
}
export interface MyGrant { owner: string; layouts: string[] }
/** What (if anything) has been shared with me for editing. */
export async function myEditGrant(uid: string): Promise<MyGrant | null> {
  try {
    const d = await firebase.firestore().collection('grants').doc(uid).get();
    const x = d.exists ? d.data()! : null;
    return x && x.mode === 'edit' && x.exp > Date.now() && Array.isArray(x.layouts) ? { owner: x.uid, layouts: x.layouts } : null;
  } catch { return null; }
}
export interface EditorInfo { deviceUid: string; editor: string; layouts: string[]; created: number | null }
/** Owner: everyone invited to edit (any of) my layouts. */
export async function listEditors(ownerUid: string): Promise<EditorInfo[]> {
  const snap = await firebase.firestore().collection('grants').where('uid', '==', ownerUid).get();
  return snap.docs.filter(d => d.data().mode === 'edit' && d.data().exp > Date.now())
    .map(d => ({ deviceUid: d.id, editor: d.data().editor || '?', layouts: d.data().layouts || [], created: d.data().created || null }))
    .sort((a, b) => (b.created || 0) - (a.created || 0));
}
export async function revokeEditor(deviceUid: string): Promise<void> {
  await firebase.firestore().collection('grants').doc(deviceUid).delete();
}

/* ---------------- presence (collaborators' cursors) ---------------- */
const PRESENCE_TTL_MS = 2 * 60 * 1000;
const presenceCol = (owner: string, name: string) =>
  firebase.firestore().collection('users').doc(owner).collection('layouts').doc(name).collection('presence');
export function publishPresence(owner: string, name: string, uid: string, c: { x: number; y: number; name: string; color: string }): Promise<void> {
  return presenceCol(owner, name).doc(uid).set({ ...c, ts: Date.now(), expires: ttlStamp(PRESENCE_TTL_MS) });
}
export function clearPresence(owner: string, name: string, uid: string): Promise<void> {
  return presenceCol(owner, name).doc(uid).delete().catch(() => {});
}
export function watchPresence(owner: string, name: string, cb: (all: Map<string, { x: number; y: number; name: string; color: string; ts: number }>) => void): () => void {
  return presenceCol(owner, name).onSnapshot(s => {
    const m = new Map<string, { x: number; y: number; name: string; color: string; ts: number }>();
    s.docs.forEach(d => { const x = d.data(); m.set(d.id, { x: +x.x || 0, y: +x.y || 0, name: String(x.name || ''), color: String(x.color || '#888'), ts: +x.ts || 0 }); });
    cb(m);
  }, () => cb(new Map()));
}

/** Storage for an invited editor: the owner's account, limited to the
    layouts named in the grant (the rules allow nothing else). */
export class CollabBackend implements StoreBackend {
  kind = 'cloud' as const;
  private inner: CloudBackend;
  constructor(readonly ownerUid: string, readonly names: string[]) { this.inner = new CloudBackend(ownerUid); }
  private doc(name: string) { return firebase.firestore().collection('users').doc(this.ownerUid).collection('layouts').doc(name); }
  async list(): Promise<LayoutMeta[]> {
    const metas: LayoutMeta[] = [];
    for (const name of this.names) {
      try { const d = await this.doc(name).get(); if (d.exists && typeof d.data()!.data === 'string') metas.push({ name, mtime: d.data()!.mtime || 0 }); } catch { /* revoked or gone */ }
    }
    return metas.sort(byMtimeDesc);
  }
  load(name: string) { return this.names.includes(name) ? this.inner.load(name) : Promise.resolve(null); }
  save(name: string, json: string, mtime?: number) { return this.inner.save(name, json, mtime); }
  async remove(): Promise<void> { throw new Error('Only the owner can delete this layout.'); }
  watchList(cb: (list: LayoutMeta[]) => void): () => void {
    const metas = new Map<string, LayoutMeta>();
    const emit = () => cb([...metas.values()].sort(byMtimeDesc));
    const offs = this.names.map(name => this.doc(name).onSnapshot(s => {
      if (s.exists && typeof s.data()!.data === 'string') metas.set(name, { name, mtime: s.data()!.mtime || 0 }); else metas.delete(name);
      emit();
    }, () => { metas.delete(name); emit(); }));
    return () => offs.forEach(f => f());
  }
  listVersions(name: string) { return this.inner.listVersions(name); }
  loadVersion(name: string, id: string) { return this.inner.loadVersion(name, id); }
  saveVersion(name: string, json: string, mtime: number) { return this.inner.saveVersion(name, json, mtime); }
}

/** Client error report → `errors` collection (create-only by rules). */
export async function sendErrorReport(rec: Record<string, unknown>): Promise<void> {
  await firebase.firestore().collection('errors').add(rec);
}

/* ---------------- sharing ----------------
 * shares/{id} = {uid, name} pointer under a random unguessable id; the layout
 * doc carries {shared: true, shareId}. Recipients (no account needed) resolve
 * the pointer, then read/watch the layout doc directly — the rules allow
 * public `get` only while shared == true. */

const layoutDoc = (uid: string, name: string) =>
  firebase.firestore().collection('users').doc(uid).collection('layouts').doc(name);

function newShareId(): string {
  const b = new Uint8Array(14);
  crypto.getRandomValues(b);
  return Array.from(b, x => 'abcdefghijklmnopqrstuvwxyz0123456789'[x % 36]).join('');
}

/** Create (or re-point) a share. Returns the share id. */
export async function createShare(uid: string, name: string, id?: string): Promise<string> {
  const db = firebase.firestore();
  const shareId = id || newShareId();
  const batch = db.batch();
  batch.set(db.collection('shares').doc(shareId), { uid, name });
  batch.set(layoutDoc(uid, name), { shared: true, shareId }, { merge: true });
  await batch.commit();
  return shareId;
}

/** The layout's active share id, or null if it isn't shared. */
export async function getShareId(uid: string, name: string): Promise<string | null> {
  const d = await layoutDoc(uid, name).get();
  if (!d.exists) return null;
  const x = d.data()!;
  return (x.shared && x.shareId) || null;
}

export async function revokeShare(uid: string, name: string): Promise<void> {
  const db = firebase.firestore();
  const d = await layoutDoc(uid, name).get();
  const id = d.exists ? (d.data()!.shareId as string | undefined) : undefined;
  const batch = db.batch();
  if (id) batch.delete(db.collection('shares').doc(id));
  // update, never set+merge: a deleted layout must not come back as a ghost
  if (d.exists) batch.update(layoutDoc(uid, name), { shared: false, shareId: firebase.firestore.FieldValue.delete() });
  await batch.commit();
}

export async function resolveShare(shareId: string): Promise<{ uid: string; name: string } | null> {
  try {
    const d = await firebase.firestore().collection('shares').doc(shareId).get();
    return d.exists ? (d.data() as { uid: string; name: string }) : null;
  } catch { return null; }
}

export async function loadSharedLayout(uid: string, name: string): Promise<LoadResult | null> {
  try {
    const d = await layoutDoc(uid, name).get();
    if (!d.exists) return null;
    const x = d.data()!;
    return { doc: JSON.parse(x.data), mtime: x.mtime || 0 };
  } catch { return null; }
}

/** Live-follow a shared layout; cb fires on every remote change. */
export function watchSharedLayout(uid: string, name: string,
                                  cb: (rec: LoadResult) => void): () => void {
  return layoutDoc(uid, name).onSnapshot(s => {
    if (!s.exists) return;
    const x = s.data()!;
    try { cb({ doc: JSON.parse(x.data), mtime: x.mtime || 0 }); } catch { /* mid-write */ }
  }, () => { /* revoked or transient — viewer keeps the last state */ });
}

/* ---------------- headset pairing ----------------
 * TV-style device flow, no server needed. The headset signs in ANONYMOUSLY
 * (no credentials typed in-headset) and publishes pairs/{code} =
 * {deviceUid, exp} with a short code. The owner types that code in the
 * editor, which writes grants/{deviceUid} = {uid, exp}: the rules then let
 * that device get/list/watch the owner's layouts — never write. Revoking =
 * deleting the grant; codes expire in 10 min, grants in 90 days. */

const PAIR_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';   // no 0/O/1/I/L lookalikes
const PAIR_TTL_MS = 10 * 60 * 1000;
const GRANT_TTL_MS = 90 * 24 * 3600 * 1000;
/** `expires` mirrors `exp` as a Timestamp: Firestore's TTL policy on that
    field deletes stale codes/grants for us (rules still enforce `exp`). */
const ttlStamp = (ms: number) => firebase.firestore.Timestamp.fromMillis(Date.now() + ms);

export async function signInAnonymouslyIfNeeded(): Promise<firebase.User> {
  const u = firebase.auth().currentUser;
  if (u) return u;
  return (await firebase.auth().signInAnonymously()).user!;
}

/** Headset: publish a fresh pairing code for this device (valid 10 min). */
export async function publishPairCode(deviceUid: string, device?: string): Promise<string> {
  const b = new Uint8Array(6);
  crypto.getRandomValues(b);
  const code = Array.from(b, x => PAIR_ALPHABET[x % PAIR_ALPHABET.length]).join('');
  await firebase.firestore().collection('pairs').doc(code)
    .set({ deviceUid, exp: Date.now() + PAIR_TTL_MS, expires: ttlStamp(PAIR_TTL_MS), ...(device ? { device } : {}) });
  return code;
}

export interface Grant { uid: string; name: string | null }

/** Headset: live-watch for the owner's grant. cb gets {uid, name} (or null). */
export function watchGrant(deviceUid: string, cb: (grant: Grant | null) => void): () => void {
  return firebase.firestore().collection('grants').doc(deviceUid).onSnapshot(s => {
    const x = s.exists ? s.data()! : null;
    cb(x && x.exp > Date.now() ? { uid: x.uid, name: x.name || null } : null);
  }, () => cb(null));
}

/** Headset: this device's currently valid grant, if any. */
export async function getGrant(deviceUid: string): Promise<Grant | null> {
  try {
    const d = await firebase.firestore().collection('grants').doc(deviceUid).get();
    const x = d.exists ? d.data()! : null;
    return x && x.exp > Date.now() ? { uid: x.uid, name: x.name || null } : null;
  } catch { return null; }
}

/** Editor (owner): redeem a typed code → grant that device read-only access. */
export async function grantPairCode(ownerUid: string, rawCode: string, ownerName?: string): Promise<boolean> {
  const code = rawCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!code) return false;
  try {
    const d = await firebase.firestore().collection('pairs').doc(code).get();
    if (!d.exists) return false;
    const x = d.data()!;
    if (!x.deviceUid || x.exp < Date.now()) return false;
    await firebase.firestore().collection('grants').doc(x.deviceUid)
      .set({ uid: ownerUid, exp: Date.now() + GRANT_TTL_MS, expires: ttlStamp(GRANT_TTL_MS), created: Date.now(),
             ...(ownerName ? { name: ownerName } : {}), ...(x.device ? { device: x.device } : {}) });
    return true;
  } catch { return false; }
}

export interface GrantInfo { deviceUid: string; device: string | null; created: number | null; exp: number }

/** Editor (owner): every headset currently paired to this account. */
export async function listGrants(ownerUid: string): Promise<GrantInfo[]> {
  const snap = await firebase.firestore().collection('grants').where('uid', '==', ownerUid).get();
  return snap.docs
    .map(d => ({ deviceUid: d.id, device: d.data().device || null,
                 created: d.data().created || null, exp: d.data().exp || 0 }))
    .filter(g => g.exp > Date.now())
    .sort((a, b) => (b.created || 0) - (a.created || 0));
}

/** Editor (owner): revoke every paired headset. Returns how many. */
export async function revokeAllGrants(ownerUid: string): Promise<number> {
  const snap = await firebase.firestore().collection('grants').where('uid', '==', ownerUid).get();
  if (!snap.size) return 0;
  const batch = firebase.firestore().batch();
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
  return snap.size;
}

/** Headset: drop this device's own grant. */
export async function unpairDevice(deviceUid: string): Promise<void> {
  try { await firebase.firestore().collection('grants').doc(deviceUid).delete(); } catch { /* already gone */ }
}

/** Headset: tidy up a redeemed/abandoned pairing code (rules: own codes only). */
export async function deletePairCode(code: string): Promise<void> {
  try { await firebase.firestore().collection('pairs').doc(code).delete(); } catch { /* already gone */ }
}
